import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useState, type ReactElement } from 'react';

import { AuthProvider, useAuth } from '../lib/auth';
import { AdmissionPage } from './pages/AdmissionPage';
import { ChildProfilePage } from './pages/ChildProfilePage';
import { ParentHomePage } from './pages/ParentHomePage';
// Document-upload module parked — restore this import with the route below.
// import { DocumentsPage } from './pages/DocumentsPage';
import { HelpPage } from './pages/HelpPage';
import { LoginPage } from './pages/LoginPage';
import { PartialApprovalPage } from './pages/PartialApprovalPage';
import { PaymentPage } from './pages/PaymentPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { ProfilePage } from './pages/ProfilePage';
import { ReceiptPage } from './pages/ReceiptPage';
import { RegisterPage } from './pages/RegisterPage';
import { SubjectFeePaymentPage } from './pages/SubjectFeePaymentPage';
import { SubjectSelectionPage } from './pages/SubjectSelectionPage';

function ParentRoutes(): ReactElement {
  const { loading, token } = useAuth();

  if (loading) {
    return (
      <main className="shell">
        <p>Loading your session…</p>
      </main>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/register/payment" element={<PaymentPage />} />
      <Route
        path="/applications/:applicationId/subjects"
        element={token ? <SubjectSelectionPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/applications/:applicationId/partial-approval"
        element={token ? <PartialApprovalPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/applications/:applicationId/payment"
        element={token ? <SubjectFeePaymentPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/applications/:applicationId/admission"
        element={token ? <AdmissionPage /> : <Navigate to="/login" replace />}
      />
      {/* Document-upload module parked.
      <Route
        path="/applications/:applicationId/documents"
        element={token ? <DocumentsPage /> : <Navigate to="/login" replace />}
      />
      */}
      <Route
        path="/dashboard"
        element={token ? <ParentHomePage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/children/:learnerId"
        element={token ? <ChildProfilePage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/payments"
        element={token ? <PaymentsPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/profile"
        element={token ? <ProfilePage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/help"
        element={token ? <HelpPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/receipts/:paymentId"
        element={token ? <ReceiptPage /> : <Navigate to="/login" replace />}
      />
      <Route path="/" element={<Navigate to={token ? '/dashboard' : '/login'} replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Views whose content moves without the parent touching anything: a reviewer
 * approves or returns an application in a session of their own, and there is no
 * channel that pushes that here. These keys poll while the tab is in front
 * (React Query holds the timer while it is hidden) and refresh on focus, so a
 * decision lands without the parent having to reload the page.
 */
const LIVE_QUERY_KEYS = [
  ['my-learners'],
  ['learner-detail'],
  ['parent-home'],
  ['application'],
  ['bag'],
  ['registration-schools'],
];
const LIVE_POLL_MS = 15_000;

export function ParentApp(): ReactElement {
  const [queryClient] = useState(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: { retry: 1, refetchOnWindowFocus: false },
      },
    });
    for (const key of LIVE_QUERY_KEYS) {
      client.setQueryDefaults(key, {
        refetchInterval: LIVE_POLL_MS,
        refetchOnWindowFocus: true,
      });
    }
    return client;
  });

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ParentRoutes />
      </AuthProvider>
    </QueryClientProvider>
  );
}
